import { Backdrop, makeStyles, Modal } from '@material-ui/core'
import clsx from 'clsx'
import Switch from 'rc-switch'
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { COLORS } from '../../consts'
import {
    CustomerTaxCertificate,
    getCustomerTaxCertificateByCustomerAccountCode,
    fetchGetContact,
} from '../../features/customer/customerAPI'
import { openRecipient, selectFilteredContacts } from '../../features/customer/customerSlice'
import { closeModals, MODALS, setHistoryOpen, setModal } from '../../features/meta/metaSlice'
import {
    fetchQuoteUser,
    fetchWarehouse,
    OrderHistoryReturn,
    QuoteUserInfo,
    WarehouseReturnPayload,
} from '../../features/order/orderAPI'
import {
    deleteAttachment,
    getOrderMiscInfo,
    loadHistory,
    LOADING_STATES,
    quitQuote,
    // setAddLineItemLoading,
    setCcContacts,
    setShowImages,
    setToContact,
    updateAttachment,
    updateCartName,
    uploadAttachment,
} from '../../features/order/orderSlice'
import { AWCButton, BUTTON_TYPES, ToggledInput } from '../../GlobalComponents/Inputs/Inputs'
import { logEvent } from '../../GlobalComponents/Logger'
import { TextOverflow } from '../../GlobalComponents/_misc/TextOverflow'
import { formatContact, toLowerEquals } from '../../utils'
import { AttachmentModal } from './AttachmentModal'
import { CustomerTypeModal } from './CustomerTypeModal'
import EmailSearch from './EmailSearch'
import IdentifierModal from './IdentifierModal'
import LineItems from './LineItems'
import styles from './OrderScreen.module.scss'
import { OrderScreenHeaderFastQuote, OrderScreenHeaderReviewQuote } from './OrderScreenHeader'
import RecipientModal, { AddContactModal } from './RecipientModal'
import { SearchQuotesModal } from './SearchQuotesModal'
import { ImportBOMModal } from './ImportBOMModal'
import ShippingModal from './ShippingModal'
import { UniqueCustomerNameModal } from './UniqueCustomerNameModal'
import _ from 'lodash'

// import metaSlice from '../features/meta/metaSlice'
// import { current } from '@reduxjs/toolkit'

// import { current } from '@reduxjs/toolkit'
const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: 0,
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        // border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
}))

const renderModal = (modal: MODALS, props: any) => {
    if (modal === MODALS.MODIFY) return <ShippingModal {...props} />
    else if (modal === MODALS.UPDATE) return <IdentifierModal {...props} />
    else if (modal === MODALS.CUSTOMER_TYPE) return <CustomerTypeModal {...props} />
    else if (modal === MODALS.UNIQUE_CUSTOMER_NAME) return <UniqueCustomerNameModal {...props} />
    else if (modal === MODALS.RECIPIENT) return <RecipientModal {...props} />
    else if (modal === MODALS.ADD_CONTACT) return <AddContactModal {...props} />
}

interface OrderScreenProps {
    reviewingQuoteOutput: boolean
    setReviewingQuoteOutput: Dispatch<SetStateAction<boolean>>
}

interface RequirementsBubbleProps {
    accountCode: string
}

export const BOMField = () => {
    const cartName = useAppSelector((state) => state.order.orderHeader[0].CartName)
    const dispatch = useAppDispatch()
    return (
        <div style={{ display: 'flex', alignItems: 'center', height: 32 }}>
            <div style={{ marginRight: 5 }}>BOM Name:</div>
            <ToggledInput
                value={cartName}
                editTestId={'editBOM'}
                doneTestId={'doneBOM'}
                activeText={'Done'}
                onChange={(newVal: string) => {
                    dispatch(updateCartName(newVal))
                }}
            />
        </div>
    )
}

interface OrderScreenNotesProps {
    currentTaxCertificates?: CustomerTaxCertificate[] | null
    expiredTaxCertificates?: CustomerTaxCertificate[] | null
}

const OrderScreenNotes = (props: OrderScreenNotesProps) => {
    const { currentTaxCertificates, expiredTaxCertificates } = props

    const orderHeader = useAppSelector((state) => state.order.orderHeader)
    const orderMiscInfo = useAppSelector((state) => state.order.orderMiscInfo)
    const showImages = useAppSelector((state) => state.order.showImages)

    const isLargeView = useMediaQuery({
        query: '(min-width: 1484px)',
    })

    const [afterNeedDateExists, setAfterNeedDateExists] = useState<boolean>(false)

    useEffect(() => {
        if (orderHeader && orderHeader.length > 0) {
            const orderDetails = orderHeader[0].OrderDetailView

            orderDetails.forEach((dataItem: any) => {
                if (!dataItem.temp) {
                    const value = dataItem.ExpectedOnDockDate
                        ? new Date(dataItem.ExpectedOnDockDate)
                        : null
                    const initialValue = dataItem.NeedDate ? new Date(dataItem.NeedDate) : null

                    const estimatedDate = value || initialValue

                    const needDate = dataItem.OriginalOnDockDate
                        ? new Date(dataItem.OriginalOnDockDate)
                        : null

                    if (estimatedDate && needDate && estimatedDate > needDate) {
                        setAfterNeedDateExists(true)
                    }
                }
            })
        }
    }, [])

    return (
        <>
            <div style={isLargeView ? { marginLeft: '5%', marginTop: 50 } : { marginTop: 50 }}>
                <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 10 }}>Notes</div>
                {!expiredTaxCertificates && !currentTaxCertificates ? (
                    <div className={styles.notesLine}>
                        1. If tax exempt and certificate not on file or expired, please provide a
                        tax-exempt certificate with the purchase order.
                    </div>
                ) : expiredTaxCertificates && expiredTaxCertificates.length > 0 ? (
                    <div className={styles.notesLine}>
                        1. If tax exempt and certificate not on file or expired, please provide a
                        tax-exempt certificate with the purchase order.
                    </div>
                ) : (
                    <div className={styles.notesLine}>
                        1. If tax exempt, please provide a tax-exempt certificate with the purchase
                        order.
                    </div>
                )}

                <div className={styles.notesLine}>
                    2. Estimated delivery is based upon availability at the time of quotation.
                    Click&nbsp;
                    <AWCButton
                        type={BUTTON_TYPES.LINK}
                        onClick={() => {
                            window.open('http://www.awc-inc.com/')
                        }}
                    >
                        Update Online&nbsp;
                    </AWCButton>
                    for current availability.{' '}
                    {afterNeedDateExists && (
                        <>
                            Date in <span style={{ color: 'red' }}>red</span> is after the need
                            date.
                        </>
                    )}
                </div>

                <div className={styles.notesLine}>
                    3. All stock subject to prior sale. Stock material generally ships the same day
                    if purchase orders are received by 2:00 PM EST.
                </div>
                <div className={styles.notesLine}>
                    4. Unless otherwise agreed to in writing by both parties, all sales resulting
                    from this quote are subject to AWC's{' '}
                    <AWCButton
                        type={'link'}
                        style={{ display: 'inline' }}
                        onClick={() => {
                            window.open('http://www.awc-inc.com/AWCTermsAndConditions.pdf')
                        }}
                    >
                        Terms &amp; Conditions
                    </AWCButton>
                    .
                </div>
                <div className={styles.notesLine}>
                    5. The quoted subtotal does not include import/export duties or credit card
                    processing charges.
                </div>
                <div className={styles.notesLine}>
                    6. Credit card payments are acceptable if made at time of order placement,
                    subject to a maximum of $10,000. A credit card processing charge of up to 3% of
                    the order amount may be added to the order total.
                </div>
                {orderMiscInfo && orderMiscInfo.OrderValue.FreightEstimate ? (
                    <div className={styles.notesLine}>
                        7. Estimated freight charges are based upon published rates and any
                        applicable handling charges.
                    </div>
                ) : (
                    <div className={styles.notesLine}>
                        7. We are unable to estimate the freight. We apologize for the
                        inconvenience. Freight will be determined at time of purchase order.
                    </div>
                )}
                <div className={styles.notesLine}>
                    8. Online access is limited to registered users. Please contact your AWC
                    representative to register.
                </div>
                {showImages && (
                    <div className={styles.notesLine}>
                        9. Images shown above are for illustration purposes only.
                    </div>
                )}
            </div>
        </>
    )
}

export const RequirementsBubble = (props: RequirementsBubbleProps) => {
    const { accountCode } = props
    const requirements = useAppSelector(
        (state) => state.customer.customerRequirements[accountCode] || []
    )
    const [moreOpen, setMoreOpen] = useState(false)
    return (
        <div
            data-testid='requirementsBubble'
            onMouseOver={(e) => e.stopPropagation()}
            // style={{ height: 'fit-content', maxWidth: 620, width: 'max-content' }}
            style={{
                overflowX: 'hidden',
                overflowY: 'auto',
                maxWidth: 620,
                width: 'max-content',
                maxHeight: 250,
            }}
            className={styles.identifierBubble}
        >
            <div style={{ fontWeight: 500 }}>Requirements: </div>

            <ul style={{ flexDirection: 'column', listStyleType: 'initial' }}>
                {requirements.length > 0 ? (
                    (moreOpen ? requirements : requirements.slice(0, 5)).map((row) => (
                        <li data-testid='requirementsRow'>{row.Requirements}</li>
                    ))
                ) : (
                    <li>None</li>
                )}
                {/* <li>All tickets printed in Whse 14 are to ship via our truck PA;</li>
                <li>Deliveries on Tuesdays & Thursday</li>
                <li>Do not invoice standard inbound freight.</li>
                <li>MICRON QUOTE#: SOQ0006642</li>
                <li>DO NOT CHARGE A HANDLING FEE</li> */}
            </ul>
            {!moreOpen && requirements.length > 5 && (
                <AWCButton
                    type={BUTTON_TYPES.LINK}
                    onClick={() => setMoreOpen(true)}
                    data-testid='requirementsViewMore'
                >
                    View More
                </AWCButton>
            )}
        </div>
    )
}
const getVendorIdsFromString = (str: string) => {
    return str
        .split('|')
        .filter((row) => row)
        .map((row) => row.split('`'))
        .map((row) => ({ id: row[0], name: row[1] }))
}
const cleanVendorIds = (vendorIdString: string) => {
    let vendorIds = getVendorIdsFromString(vendorIdString)
    return vendorIds
        .slice(0, 4)
        .map((row) => row.id)
        .join(', ')
}
export const getIconClass = (color: COLORS) => {
    if (toLowerEquals(color, COLORS.RED)) return styles.dangerIcon
    else if (toLowerEquals(color, COLORS.YELLOW)) return styles.uncertainIcon
    else if (toLowerEquals(color, COLORS.GREY)) return styles.infoIcon
    else return styles.checkmarkIcon
}

const OrderScreen = (props: OrderScreenProps) => {
    const { reviewingQuoteOutput, setReviewingQuoteOutput } = props
    let currentAccount = useAppSelector((state) => state.order.currentAccount!)
    let toContact = useAppSelector((state) => state.order.toContact)
    const [toContactExtended, setToContactExtended] = useState<ContactViewRow>()
    const [quoteUser, setQuoteUser] = useState<QuoteUserInfo>()
    const [warehouse, setWarehouse] = useState<WarehouseReturnPayload>()

    let ccContacts = useAppSelector((state) => state.order.ccContacts)
    const dispatch = useAppDispatch()
    let modal = useAppSelector((state) => state.meta.currentModal)
    const classes = useStyles()
    const contacts = useAppSelector((state) =>
        selectFilteredContacts(state, state.order.currentAccount!.AccountCode, '')
    )
    const [singleChoice, setSingleChoice] = useState(false)
    const [attachmentModalOpen, setAttachmentModalOpen] = useState(false)
    const [importBOMModalOpen, setImportBOMModalOpen] = useState(false)

    const openBOMModal = () => {
        setImportBOMModalOpen(true)
    }

    const lineItemsRef = useRef<typeof LineItems | null>(null)

    const historyLoading = useAppSelector((state) => state.order.orderHistoryLoading)
    const historyDetails = useAppSelector((state) => state.order.orderHistoryDetails)

    const orderHeader = useAppSelector((state) => state.order.orderHeader)
    const orderMiscInfo = useAppSelector((state) => state.order.orderMiscInfo!)
    const orderFilenames = useAppSelector((state) => state.order.orderFilenames)
    const loadingState = useAppSelector((state) => state.order.orderLoading)

    const cartName = useAppSelector((state) => state.order.orderHeader[0]?.CartName)

    // Screen size resolution
    const bigScreen = useMediaQuery({ query: '(min-width: 1484px)' })
    // const smallScreen = useMediaQuery({ query: '(max-width: 1484px)' })

    // Check if new order
    // const [isNewOrder, setIsNewOrder] = useState<boolean>(true)

    useEffect(() => {
        async function getToContactExtended() {
            let existingContact = await fetchGetContact(
                toContact?.EmailAddress!,
                toContact?.ContactKey!
            )
            if (existingContact) {
                setToContactExtended(existingContact)
            }
        }
        toContact?.EmailAddress && getToContactExtended()
    }, [toContact])

    useEffect(() => {
        async function getQuotedByUser() {
            let user: QuoteUserInfo = await fetchQuoteUser(orderHeader[0].Username!)
            if (user) {
                setQuoteUser(user)
                getWarehouseInfo(user.Warehouse)
            }
        }
        async function getWarehouseInfo(warehouseNum: string) {
            let warehouse: WarehouseReturnPayload = await fetchWarehouse(warehouseNum)
            setWarehouse(warehouse)
        }

        if (reviewingQuoteOutput) getQuotedByUser()
    }, [reviewingQuoteOutput])

    const [currentTaxCertificates, setCurrentTaxCertificates] = useState<
        CustomerTaxCertificate[] | null
    >()
    const [expiredTaxCertificates, setExpiredTaxCertificates] = useState<
        CustomerTaxCertificate[] | null
    >()

    const getCustomerTaxCertificates = async (accountCode: string) => {
        const customerTaxCertificates = await getCustomerTaxCertificateByCustomerAccountCode(
            accountCode
        )
        const tempCurrentCerts: CustomerTaxCertificate[] | null = []
        const tempExpiredCerts: CustomerTaxCertificate[] | null = []

        if (!customerTaxCertificates) return
        customerTaxCertificates.forEach((cert: CustomerTaxCertificate) => {
            const certExpirationDate = cert.ExpirationDate
            const today = new Date()
            if (new Date(certExpirationDate) < today) tempExpiredCerts.push(cert)
            else tempCurrentCerts.push(cert)
        })

        const currentCerts = tempCurrentCerts.sort((a, b) => {
            if (a.ExposureZone < b.ExposureZone) return -1

            if (a.ExposureZone > b.ExposureZone) return 1

            return 0
        })

        const expiredCerts = tempExpiredCerts.sort((a, b) => {
            if (a.ExposureZone < b.ExposureZone) return -1

            if (a.ExposureZone > b.ExposureZone) return 1

            return 0
        })

        setCurrentTaxCertificates(currentCerts)
        setExpiredTaxCertificates(expiredCerts)
    }

    // if string its also a filter
    const [searchModalOpen, setSearchModalOpen] = useState<
        boolean | { field: keyof OrderHistoryReturn[number]; value: string }
    >(false)
    const [searchSelectedOrder, setSearchSelectedOrder] = useState<null | string>(null)
    const showImages = useAppSelector((state) => state.order.showImages)
    const customerMode = useAppSelector((state) => state.meta.customerMode)
    const previewMode = useAppSelector((state) => state.meta.previewMode)
    const addLineItemFromBOMLoading = useAppSelector(
        (state) => state.order.addLineItemFromBOMLoading
    )
    if (customerMode || previewMode) dispatch(setShowImages(false))

    const isLargeView = useMediaQuery({
        query: '(min-width: 1484px)',
    })

    const historyOpen = useAppSelector((state) => state.meta.historyOpen)
    useEffect(() => {
        setSearchModalOpen(historyOpen)
        setSearchSelectedOrder(null)
    }, [historyOpen])

    useEffect(() => {
        searchModalOpen && logEvent({ EventName: 'Fast Quote Search Modal Open' })
    }, [searchModalOpen])

    useEffect(() => {
        if (orderHeader.length === 0) return

        if (addLineItemFromBOMLoading !== LOADING_STATES.LOADING)
            dispatch(getOrderMiscInfo(reviewingQuoteOutput))
        getCustomerTaxCertificates(orderHeader[0].CustomerAccountCode)
    }, [orderHeader])

    // TODO: this useeffect is getting triggered twice since we are updating our orderHeader with a new shipmentMethodDescription ^^
    const addAttachment = (fileName: string, contentType: string, dataURL: string) => {
        dispatch(uploadAttachment({ fileName, contentType, fileData: dataURL }))
    }
    const removeAttachment = (fileId: number) => {
        dispatch(deleteAttachment(fileId))
    }
    const updateAttachmentInternal = (fileId: number, internal: boolean) => {
        dispatch(updateAttachment({ fileId, internal }))
    }

    modal = toContact || previewMode ? modal : MODALS.RECIPIENT
    let orderDetail = useAppSelector((state) =>
        state.order.orderHeader[0] ? state.order.orderHeader[0].OrderDetailView : []
    )
    let invalidLines = useAppSelector((state) => state.order.invalidLines)
    let hasValidPart = orderDetail.some(
        (row) =>
            row.PartKey &&
            row.PartKey > 0 &&
            (!invalidLines[row.rowId] || !invalidLines[row.rowId].partNumber)
    )
    if (loadingState !== LOADING_STATES.DONE)
        return (
            <div className={'centered'}>
                <div className={'k-i-loading'} style={{ fontSize: 200, height: '90vh' }} />
            </div>
        )
    let orderMarginPercentage = 0
    if (orderMiscInfo && orderMiscInfo.OrderValue) {
        const { Cost, SubTotal, RebateTotal } = orderMiscInfo.OrderValue
        const orderMargin = SubTotal === 0 ? 0 : (SubTotal - (Cost - RebateTotal)) / SubTotal
        orderMarginPercentage = Math.round(orderMargin * 100)
    }

    interface OrderScreenHeaderReviewQuoteNotesProps {
        specialInstructions: string | undefined
        quoteOpenNotes: string | undefined
    }

    const OrderScreenHeaderReviewQuoteNotes = (props: OrderScreenHeaderReviewQuoteNotesProps) => {
        if (
            (!previewMode || !props.specialInstructions || props.specialInstructions === '') &&
            (!props.quoteOpenNotes || props.quoteOpenNotes === '')
        )
            return <></>

        return (
            <div className={clsx([styles.reviewQuoteNotesSection])}>
                <div className={clsx([styles.reviewQuoteNotesHeader])}>Note</div>
                {props.quoteOpenNotes && props.quoteOpenNotes > '' && (
                    <div>{props.quoteOpenNotes}</div>
                )}
                {props.quoteOpenNotes &&
                    props.quoteOpenNotes > '' &&
                    previewMode &&
                    props.specialInstructions &&
                    props.specialInstructions > '' && (
                        <div
                            className={'a-divider'}
                            style={{ margin: '0 12px', padding: 0, width: '100px' }}
                        />
                    )}
                {previewMode && props.specialInstructions && props.specialInstructions > '' && (
                    <div>{props.specialInstructions}</div>
                )}
            </div>
        )
    }

    if (!reviewingQuoteOutput) {
        return (
            <div className={styles.orderScreen} data-testid={'orderScreen'}>
                <div style={bigScreen ? { marginRight: 30, marginTop: 10 } : {}}>
                    <div style={{ position: 'relative' }}>
                        <div>
                            {historyLoading === LOADING_STATES.LOADING || !historyDetails ? (
                                <>
                                    {historyLoading === LOADING_STATES.LOADING && (
                                        <div className={styles.historyDetail}>
                                            <div
                                                style={{
                                                    fontSize: 40,
                                                    height: 40,
                                                    marginRight: 100,
                                                }}
                                                className={'k-i-loading'}
                                            />
                                        </div>
                                    )}
                                    {historyLoading === LOADING_STATES.DONE && !historyDetails && (
                                        <></>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className={styles.historyDetail2}>
                                        <div className={styles.historyHeader}>
                                            <div>Previous Quotes &#38; Orders</div>
                                        </div>

                                        <div className={styles.lines}>
                                            <div
                                                className={styles.line}
                                                onClick={(e) => {
                                                    setSearchModalOpen({
                                                        field: 'ContactEmailAddress',
                                                        value: historyDetails.contact,
                                                    })
                                                    setSearchSelectedOrder(
                                                        historyDetails.contactMostRecent
                                                            ?.OrderNumber || null
                                                    )

                                                    e.stopPropagation()
                                                }}
                                            >
                                                <div className={styles.selectedSearch}>
                                                    <span className='k-icon k-i-search'></span>
                                                </div>
                                                <div>Search by contact email:</div>
                                                <div style={{ display: 'flex' }}>
                                                    <div
                                                        style={{ fontWeight: 400, paddingRight: 5 }}
                                                    >
                                                        {/* {historyDetails.contact}&nbsp; */}
                                                        <TextOverflow
                                                            text={historyDetails.contact || ''}
                                                            font={'14px roboto'}
                                                            maxWidth={300}
                                                        />
                                                    </div>

                                                    <div className={styles.lightHistoryLine}>
                                                        {historyDetails.contactMostRecent && (
                                                            <>
                                                                {' '}
                                                                (
                                                                {historyDetails.contactMostRecent
                                                                    ? new Date(
                                                                          historyDetails.contactMostRecent.OrderDate
                                                                      ).toLocaleDateString(
                                                                          'en-US',
                                                                          {
                                                                              month: '2-digit',
                                                                              day: '2-digit',
                                                                              year: '2-digit',
                                                                          }
                                                                      )
                                                                    : ''}
                                                                &nbsp;
                                                                {historyDetails.contactMostRecent?.OrderType.includes(
                                                                    'Quote'
                                                                )
                                                                    ? 'Quote'
                                                                    : 'Order'}
                                                                <span
                                                                    style={{ padding: '0px 2px' }}
                                                                >
                                                                    -
                                                                </span>
                                                                {historyDetails.contactMostRecent
                                                                    ?.VendorId &&
                                                                    cleanVendorIds(
                                                                        historyDetails
                                                                            .contactMostRecent
                                                                            .VendorId
                                                                    )}
                                                                )
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                className={styles.line}
                                                onClick={(e) => {
                                                    setSearchModalOpen({
                                                        field: 'EntityName',
                                                        value: historyDetails.customer,
                                                    })
                                                    setSearchSelectedOrder(
                                                        historyDetails.customerMostRecent
                                                            ?.OrderNumber || null
                                                    )
                                                    e.stopPropagation()
                                                }}
                                            >
                                                <div className={styles.selectedSearch}>
                                                    <span className='k-icon k-i-search'></span>
                                                </div>
                                                <div>Search by unique customer:</div>
                                                <div style={{ display: 'flex' }}>
                                                    <div
                                                        style={{ fontWeight: 400, paddingRight: 5 }}
                                                    >
                                                        {/* {historyDetails.customer}&nbsp; */}
                                                        <TextOverflow
                                                            text={historyDetails.customer || ''}
                                                            font={'14px roboto'}
                                                            maxWidth={300}
                                                        />
                                                    </div>
                                                    <div className={styles.lightHistoryLine}>
                                                        {historyDetails.customerMostRecent && (
                                                            <>
                                                                (
                                                                {historyDetails.customerMostRecent
                                                                    ? new Date(
                                                                          historyDetails.customerMostRecent.OrderDate
                                                                      ).toLocaleDateString(
                                                                          'en-US',
                                                                          {
                                                                              month: '2-digit',
                                                                              day: '2-digit',
                                                                              year: '2-digit',
                                                                          }
                                                                      )
                                                                    : ''}
                                                                &nbsp;
                                                                {historyDetails.customerMostRecent?.OrderType.includes(
                                                                    'Quote'
                                                                )
                                                                    ? 'Quote'
                                                                    : 'Order'}
                                                                <span
                                                                    style={{ padding: '0px 2px' }}
                                                                >
                                                                    -
                                                                </span>
                                                                {historyDetails.customerMostRecent
                                                                    ?.VendorId &&
                                                                    cleanVendorIds(
                                                                        historyDetails
                                                                            .customerMostRecent
                                                                            .VendorId
                                                                    )}
                                                                )
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                className={clsx([styles.line, styles.lastLine])}
                                                onClick={(e) => {
                                                    setSearchModalOpen({
                                                        field: 'ContactEmailDomain',
                                                        value: historyDetails.domain,
                                                    })
                                                    setSearchSelectedOrder(
                                                        historyDetails.domainMostRecent
                                                            ?.OrderNumber || null
                                                    )

                                                    e.stopPropagation()
                                                }}
                                            >
                                                <div className={styles.selectedSearch}>
                                                    <span className='k-icon k-i-search'></span>
                                                </div>
                                                <div>Search by email domain:</div>
                                                <div style={{ display: 'flex' }}>
                                                    <div
                                                        style={{ fontWeight: 400, paddingRight: 5 }}
                                                    >
                                                        {/* {historyDetails.domain}&nbsp; */}
                                                        <TextOverflow
                                                            text={historyDetails.domain || ''}
                                                            font={'14px roboto'}
                                                            maxWidth={300}
                                                        />
                                                    </div>
                                                    <div className={styles.lightHistoryLine}>
                                                        {historyDetails.domainMostRecent && (
                                                            <>
                                                                {' '}
                                                                (
                                                                {historyDetails.domainMostRecent
                                                                    ? new Date(
                                                                          historyDetails.domainMostRecent.OrderDate
                                                                      ).toLocaleDateString(
                                                                          'en-US',
                                                                          {
                                                                              month: '2-digit',
                                                                              day: '2-digit',
                                                                              year: '2-digit',
                                                                          }
                                                                      )
                                                                    : ''}
                                                                &nbsp;
                                                                {historyDetails.domainMostRecent?.OrderType.includes(
                                                                    'Quote'
                                                                )
                                                                    ? 'Quote'
                                                                    : 'Order'}
                                                                <span
                                                                    style={{ padding: '0px 2px' }}
                                                                >
                                                                    -
                                                                </span>
                                                                {historyDetails.domainMostRecent
                                                                    ?.VendorId &&
                                                                    cleanVendorIds(
                                                                        historyDetails
                                                                            .domainMostRecent
                                                                            .VendorId
                                                                    )}
                                                                )
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className={styles.contactSection}>
                    <div style={{ display: 'flex', marginTop: 28 }}>
                        <div className={'centered'} style={{ marginRight: 10 }}>
                            <AWCButton
                                type={'secondary'}
                                onClick={() => {
                                    dispatch(openRecipient(currentAccount))
                                    setSingleChoice(true)
                                }}
                                style={{ width: 42, height: 31 }}
                                data-testid={'toButton'}
                            >
                                To:
                            </AWCButton>
                        </div>
                        <div
                            onClick={() => {
                                dispatch(setModal(MODALS.RECIPIENT))
                                setSingleChoice(true)
                            }}
                            style={{
                                alignSelf: 'center',
                                cursor: 'pointer',
                                width: '100%',
                            }}
                            data-testid={'toField'}
                        >
                            {toContact && (
                                <TextOverflow text={formatContact(toContact)} maxWidth={400} />
                            )}
                        </div>
                    </div>
                    <div style={{ display: 'flex', marginTop: 3 }}>
                        <div className={'centered'} style={{ marginRight: 10 }}>
                            <AWCButton
                                type={'secondary'}
                                onClick={() => {
                                    dispatch(openRecipient(currentAccount))
                                    setSingleChoice(false)
                                }}
                                data-testid={'ccButton'}
                                style={{ width: 42, height: 31 }}
                            >
                                Cc:
                            </AWCButton>
                        </div>
                        <EmailSearch
                            contacts={contacts}
                            onCreateNewContact={() => {
                                dispatch(setModal(MODALS.ADD_CONTACT))
                                setSingleChoice(false)
                            }}
                            selectedContacts={ccContacts}
                            setSelectedContacts={(newContacts) =>
                                dispatch(setCcContacts(newContacts))
                            }
                            multiple
                            data-testid={'ccField'}
                        />
                    </div>
                </div>

                <div
                    style={
                        bigScreen
                            ? {
                                  marginBottom: 10,
                                  marginLeft: '5%',
                                  marginRight: '5%',
                                  fontSize: 16,
                              }
                            : { marginBottom: 5, marginLeft: '1%', fontSize: 14 }
                    }
                >
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <AWCButton
                            type={BUTTON_TYPES.LINK}
                            onClick={() => setAttachmentModalOpen(true)}
                            style={{ display: 'flex' }}
                        >
                            {/* <div className={styles.plusButton} /> */}
                            <div>Add Attachments ({orderFilenames.length})</div>
                        </AWCButton>
                    </div>
                </div>

                <OrderScreenHeaderFastQuote
                    hasValidPart={hasValidPart}
                    orderMarginPercentage={orderMarginPercentage}
                    setReviewingQuoteOutput={setReviewingQuoteOutput}
                    reviewingQuoteOutput={reviewingQuoteOutput}
                    lineItemsRef={lineItemsRef}
                    currentTaxCertificates={currentTaxCertificates}
                    expiredTaxCertificates={expiredTaxCertificates}
                    openBOMModal={openBOMModal}
                />
                <div
                    style={
                        isLargeView ? { marginLeft: '5%', marginRight: '5%', marginTop: 20 } : {}
                    }
                >
                    {isLargeView && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <AWCButton
                                    type={BUTTON_TYPES.LINK}
                                    onClick={openBOMModal}
                                    style={{ display: 'flex' }}
                                >
                                    <div>Import BOM</div>
                                </AWCButton>
                            </div>
                            <BOMField />
                        </>
                    )}
                    <LineItems
                        ref={lineItemsRef}
                        reviewingQuoteOutput={reviewingQuoteOutput}
                        accountCode={currentAccount.AccountCode}
                    />
                </div>
                <Modal
                    open={searchModalOpen !== false}
                    className={classes.modal}
                    onClose={(e: any, reason: string) => !reason && setSearchModalOpen(false)}
                    aria-labelledby='simple-modal-title'
                    aria-describedby='simple-modal-description'
                    BackdropComponent={Backdrop}
                >
                    <div>
                        <SearchQuotesModal
                            close={() => {
                                setSearchModalOpen(false)
                                dispatch(setHistoryOpen(false))
                            }}
                            onSubmit={() => {
                                setSearchModalOpen(false)
                                dispatch(setHistoryOpen(false))
                            }}
                            contactEmail={toContact?.EmailAddress || ''}
                            accountCode={currentAccount.AccountCode}
                            filter={searchModalOpen}
                            selectedOrder={searchSelectedOrder}
                        />
                    </div>
                </Modal>
                <Modal
                    open={attachmentModalOpen || modal !== MODALS.NONE}
                    className={classes.modal}
                    onClose={(e: any, reason: string) => !reason && dispatch(closeModals())}
                    aria-labelledby='simple-modal-title'
                    aria-describedby='simple-modal-description'
                    BackdropComponent={Backdrop}
                >
                    <div>
                        {attachmentModalOpen ? (
                            <AttachmentModal
                                attachments={orderFilenames}
                                close={() => setAttachmentModalOpen(false)}
                                uploadAttachment={addAttachment}
                                onDelete={removeAttachment}
                                updateIsInternal={updateAttachmentInternal}
                                reviewingQuoteOutput={reviewingQuoteOutput}
                            />
                        ) : (
                            renderModal(modal, {
                                close: () => dispatch(closeModals()),
                                onCancel: () => {
                                    dispatch(closeModals())
                                    // if we don't have a to it's first load and we should cancel to home screen
                                    if (!toContact) {
                                        dispatch(quitQuote())
                                    }
                                },
                                submit: (projectName: string) => null,
                                submitContact: (toContact: ContactCustomerAccountCodeViewRow) => {
                                    dispatch(setToContact(toContact))
                                    dispatch(
                                        loadHistory({
                                            contactEmailAddress: toContact.EmailAddress!,
                                            customerAccountCode: currentAccount.AccountCode,
                                        })
                                    )
                                    dispatch(closeModals())
                                },
                                submitContacts: (ccContacts: ContactCustomerAccountCodeViewRow[]) =>
                                    dispatch(setCcContacts(ccContacts)),
                                singleChoice: !toContact || singleChoice,
                                currentCcs: ccContacts,
                                currentTo: toContact,
                                required: !toContact,
                                customer: currentAccount,
                                reason: singleChoice ? 'To' : 'Cc',
                                onSuccess: (contact: ContactCustomerAccountCodeViewRow) => {
                                    if (singleChoice) dispatch(setToContact(contact))
                                    else dispatch(setCcContacts([...ccContacts, contact]))
                                    dispatch(closeModals())
                                },
                                onCancelClick: () => dispatch(closeModals()),
                            })
                        )}
                    </div>
                </Modal>
                <Modal
                    open={importBOMModalOpen}
                    className={classes.modal}
                    onClose={() => {
                        return false
                    }}
                    aria-labelledby='simple-modal-title'
                    aria-describedby='simple-modal-description'
                    BackdropComponent={Backdrop}
                >
                    <div>
                        <ImportBOMModal
                            close={() => setImportBOMModalOpen(false)}
                            onSubmit={() => setImportBOMModalOpen(false)}
                            orderHeader={orderHeader}
                        />
                    </div>
                </Modal>
            </div>
        )
    } else {
        return (
            <div
                className={styles.orderScreen}
                data-testid={'orderScreen'}
                style={{ margin: '18px 14px' }}
            >
                <OrderScreenHeaderReviewQuote
                    hasValidPart={hasValidPart}
                    orderMarginPercentage={orderMarginPercentage}
                    toContactExtended={toContactExtended}
                    quoteUser={quoteUser}
                    warehouse={warehouse}
                    reviewingQuoteOutput={reviewingQuoteOutput}
                    currentTaxCertificates={currentTaxCertificates}
                    expiredTaxCertificates={expiredTaxCertificates}
                />
                <div
                    style={
                        bigScreen
                            ? {
                                  marginBottom: 10,
                                  marginLeft: '5%',
                                  marginRight: '5%',
                                  marginTop: 20,
                              }
                            : { marginBottom: 5 }
                    }
                >
                    <OrderScreenHeaderReviewQuoteNotes
                        specialInstructions={orderHeader[0].SpecialInstructions}
                        quoteOpenNotes={orderHeader[0].QuoteOpenNotes}
                    />

                    <div style={{ marginTop: 10, textAlign: 'right' }}>
                        * Lead times may vary due to unforeseen supply chain constraints.
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginTop: 19,
                            fontSize: 12,
                            marginBottom: 6,
                        }}
                    >
                        {cartName !== '' && (
                            <>
                                <div style={{ marginRight: 5 }}>BOM Name:</div>
                                <span
                                    style={{
                                        fontWeight: 500,
                                    }}
                                >
                                    {cartName}
                                </span>
                            </>
                        )}
                        {!customerMode && !previewMode && (
                            <>
                                <div style={{ marginRight: 5 }}>Product Images:</div>

                                <Switch
                                    checkedChildren='Show'
                                    unCheckedChildren='Hide'
                                    checked={showImages}
                                    onChange={(value: boolean) => dispatch(setShowImages(value))}
                                />
                            </>
                        )}
                    </div>
                    <LineItems
                        ref={lineItemsRef}
                        accountCode={currentAccount.AccountCode}
                        reviewingQuoteOutput={reviewingQuoteOutput}
                    />
                </div>
                <OrderScreenNotes
                    currentTaxCertificates={currentTaxCertificates}
                    expiredTaxCertificates={expiredTaxCertificates}
                />
                <Modal
                    open={searchModalOpen !== false}
                    className={classes.modal}
                    onClose={(e: any, reason: string) => !reason && setSearchModalOpen(false)}
                    aria-labelledby='simple-modal-title'
                    aria-describedby='simple-modal-description'
                    BackdropComponent={Backdrop}
                >
                    <div>
                        <SearchQuotesModal
                            close={() => setSearchModalOpen(false)}
                            onSubmit={() => setSearchModalOpen(false)}
                            contactEmail={toContact?.EmailAddress || ''}
                            accountCode={currentAccount.AccountCode}
                            filter={searchModalOpen}
                            selectedOrder={searchSelectedOrder}
                        />
                    </div>
                </Modal>
                <Modal
                    open={attachmentModalOpen || modal !== MODALS.NONE}
                    className={classes.modal}
                    onClose={(e: any, reason: string) => !reason && dispatch(closeModals())}
                    aria-labelledby='simple-modal-title'
                    aria-describedby='simple-modal-description'
                    BackdropComponent={Backdrop}
                >
                    <div>
                        {attachmentModalOpen ? (
                            <AttachmentModal
                                attachments={orderFilenames}
                                close={() => setAttachmentModalOpen(false)}
                                uploadAttachment={addAttachment}
                                onDelete={removeAttachment}
                                updateIsInternal={updateAttachmentInternal}
                                reviewingQuoteOutput
                            />
                        ) : (
                            renderModal(modal, {
                                close: () => dispatch(closeModals()),
                                submit: (projectName: string) => null,
                                submitTo: (toContact: ContactCustomerAccountCodeViewRow) => {
                                    dispatch(setToContact(toContact))
                                    dispatch(
                                        loadHistory({
                                            contactEmailAddress: toContact.EmailAddress || '',
                                            customerAccountCode: currentAccount.AccountCode,
                                        })
                                    )
                                    dispatch(closeModals())
                                },
                                submitCcs: (ccContacts: ContactCustomerAccountCodeViewRow[]) =>
                                    dispatch(setCcContacts(ccContacts)),
                                singleChoice: !toContact || singleChoice,
                                currentCcs: ccContacts,
                                currentTo: toContact,
                                required: !toContact,
                                customer: currentAccount,
                                onSuccess: (contact: ContactCustomerAccountCodeViewRow) => {
                                    if (singleChoice) dispatch(setToContact(contact))
                                    else dispatch(setCcContacts([...ccContacts, contact]))
                                    dispatch(closeModals())
                                },
                                onCancelClick: () => dispatch(closeModals()),
                            })
                        )}
                    </div>
                </Modal>
                <Modal
                    open={importBOMModalOpen}
                    className={classes.modal}
                    onClose={() => setImportBOMModalOpen(false)}
                    aria-labelledby='simple-modal-title'
                    aria-describedby='simple-modal-description'
                    BackdropComponent={Backdrop}
                >
                    <div>
                        <ImportBOMModal
                            close={() => setImportBOMModalOpen(false)}
                            onSubmit={() => setImportBOMModalOpen(false)}
                            orderHeader={orderHeader}
                        />
                    </div>
                </Modal>
            </div>
        )
    }
}
export default OrderScreen
